import { RULES, skillDef } from '../../combat/rules';
import { PRESET_MONSTERS, heroClass, itemDef, type ItemSlot } from '../../data/presets';
import { chapterStates, nextStoryStage } from '../../systems/progression';
import { icon } from '../../../ui/icons';
import { allocatePoint, expToNext, heroPower, heroStats, skillUnlocks } from '../../systems/hero';
import type { StatAlloc } from '../../systems/model';
import { artImg, button, meter } from '../../../ui/components';
import { h } from '../../../ui/dom';
import { t, tn } from '../../../i18n';
import { crayonTitle } from '../../../ui/notebookArt';
import { canShare, shareHero } from '../../../ui/share';
import type { NotebookCtx } from './tabs';

const STAT_ROWS: { key: keyof StatAlloc; label: string; max: number; per: string; icon: string }[] = [
  { key: 'hp', label: 'HP', max: 200, per: `+${RULES.progression.hpPerPoint}`, icon: 'heart' },
  { key: 'atk', label: 'ATK', max: 40, per: '+1', icon: 'sword' },
  { key: 'def', label: 'DEF', max: 30, per: '+1', icon: 'shield' },
  { key: 'luck', label: 'LUCK', max: 30, per: '+1', icon: 'clover' },
];

const SLOT_ICON: Record<ItemSlot, string> = { weapon: 'sword', armor: 'shield', charm: 'clover' };

/** "My Notebook" title written on the left page (reference 02). */
const pageTitle = (): HTMLElement =>
  h('header', { class: 'nb-page-title' }, crayonTitle(t('My Notebook')), h('p', null, t('Hero journal and collections')));

const SLOT_LABEL: Record<ItemSlot, string> = { weapon: 'Weapon', armor: 'Armor', charm: 'Charm' };

/** Left page: hero profile with animated portrait, level, stats and equipment. */
export function renderHeroPanel(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const hero = app.store.hero;
  if (!hero) {
    return h(
      'div',
      { class: 'hero-panel empty' },
      pageTitle(),
      h('h2', { class: 'page-heading' }, t('Hero Profile')),
      h('div', { class: 'empty-frame' }, h('p', null, t('No hero drawn yet!')), h('p', { class: 'hand-hint' }, t('Every legend starts with a doodle.'))),
      button(t('Create a Hero'), { variant: 'primary', color: 'yellow', sfx: app, onClick: () => ctx.goTab('newhero') }),
    );
  }
  const stats = heroStats(hero, app.store.state.itemLevels);
  const cls = heroClass(hero.classId);
  const need = expToNext(hero.level);
  const values: Record<keyof StatAlloc, number> = { hp: stats.maxHp, atk: stats.atk, def: stats.def, luck: stats.luck };
  const skill = skillDef(hero.skill);
  const nextUnlock = skillUnlocks(hero).find((u) => u.level > hero.level);

  const statRows = STAT_ROWS.map((r) =>
    h(
      'li',
      { class: 'stat-row' },
      h('span', { class: 'stat-name' }, icon(r.icon, 'stat-icon'), r.label),
      h('span', { class: 'stat-value' }, String(values[r.key])),
      hero.statPoints > 0
        ? button('+', {
            small: true,
            color: 'green',
            title: t('Spend a point: {stat} {per}', { stat: r.label, per: r.per }),
            sfx: app,
            onClick: () =>
              app.store.update(['hero'], (s) => {
                const hh = s.heroes.find((x) => x.id === hero.id);
                if (hh && !allocatePoint(hh, r.key, app.store.state.itemLevels)) app.toast(t('{stat} is already maxed out!', { stat: r.label }), 'info');
              }),
          })
        : null,
    ),
  );

  const slots = (Object.keys(SLOT_LABEL) as ItemSlot[]).map((slot) => {
    const item = itemDef(hero.equipment[slot] ?? '');
    return h(
      'button',
      { class: `equip-slot ${item ? 'filled' : ''}`, type: 'button', title: item ? t(item.note) : t('Empty slot — open Equipment'), on: { click: () => ctx.goTab('equipment') } },
      h('span', { class: 'slot-label' }, t(SLOT_LABEL[slot])),
      item ? (artImg(app, `item:${item.id}`, 'item-icon') ?? icon(SLOT_ICON[slot], 'item-icon')) : icon(SLOT_ICON[slot], 'item-icon empty-slot-icon'),
      h('span', { class: 'slot-item' }, item ? t(item.name) : t('— empty —')),
    );
  });

  const next = nextStoryStage(app.store.state);
  const starGate = next ? null : chapterStates(app.store.state).find((c) => c.starLocked);
  const adventure = (): void => {
    if (next) void app.scenes.go('battle', { mode: 'pve', monsterId: `preset:${next}` }, 'page');
    else if (starGate) {
      app.toast(t('Collect {n} stars to open this chapter', { n: starGate.starsNeeded }), 'info');
      ctx.goTab('quests');
    } else ctx.goTab('wiki');
  };

  return h(
    'div',
    { class: 'hero-panel' },
    pageTitle(),
    h(
      'div',
      { class: 'hero-head' },
      h('div', null, h('h2', { class: 'hero-name' }, hero.name), h('p', { class: 'hero-class', style: { color: cls.color } }, t(cls.name))),
    ),
    h('div', { class: 'hero-profile' },
    h('div', { class: 'portrait-frame taped' }, h('div', { class: 'portrait-anchor', 'aria-label': t('{name}, drawn as a doodle', { name: hero.name }) })),
    h('div', { class: 'hero-details' },
    h('div', { class: 'level-tag tape-title' }, h('span', null, t('Level')), h('b', null, String(hero.level))),
    h('div', { class: 'exp-row' }, h('span', null, t('To next level:')), meter(hero.exp, need, 'exp', `${hero.exp} / ${need}`)),
    hero.statPoints > 0 ? h('p', { class: 'points-note sticky-note green' }, tn(hero.statPoints, '{n} stat point to spend!', '{n} stat points to spend!')) : null,
    // Stats stay folded away until there is a point to spend.
    h(
      'details',
      { class: 'stat-box', open: hero.statPoints > 0 },
      h('summary', null, t('Stats')),
      h('ul', { class: 'stat-list' }, statRows),
      h('p', { class: 'power-line' }, t('Power {power} · arena budget {budget}', { power: heroPower(hero, app.store.state.itemLevels), budget: RULES.budget.pvpPublicBudget })),
    ),
    h(
      'details',
      { class: 'skill-box' },
      h('summary', null, t('Skill: {name}', { name: t(skill.name) })),
      h('p', null, t(skill.description)),
      nextUnlock ? h('p', { class: 'hand-hint' }, t('New skill at level {level}: {name}', { level: nextUnlock.level, name: t(skillDef(nextUnlock.id).name) })) : null,
    ),
    )),
    h('h3', { class: 'section-title equipped-title tape-title' }, icon('chest'), t('Equipped Items')),
    h('div', { class: 'equip-row' }, slots),
    h(
      'div',
      { class: 'hero-actions' },
      button(h('span', { class: 'btn-with-icon' }, icon('mountain'), t('Adventure')), {
        variant: 'primary',
        color: 'yellow',
        title: next ? t('Next story fight: {name}', { name: t(PRESET_MONSTERS.find((p) => p.id === next)?.name ?? '') }) : starGate ? t('Collect {n} stars to open this chapter', { n: starGate.starsNeeded }) : t('Story complete — pick any monster'),
        sfx: app,
        onClick: adventure,
      }),
      button(h('span', { class: 'btn-with-icon' }, icon('crown'), t('Edit Hero')), { small: true, sfx: app, onClick: () => void app.scenes.go('creator', { heroMode: true, monsterId: hero.id }) }),
      button(h('span', { class: 'btn-with-icon' }, icon('chest'), t('Equip')), { small: true, sfx: app, onClick: () => ctx.goTab('equipment') }),
      canShare(app) ? button(h('span', { class: 'btn-with-icon' }, icon('plane'), t('Show off on Facebook')), { small: true, color: 'blue', sfx: app, onClick: () => void shareHero(app, hero) }) : null,
    ),
  );
}
